import { Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

import type { DonationMatchRequestStatus } from '@percent/admin-dashboard/api/actions/donationMatchRequests/donationMatchRequests.types'
import { ErrorView, Loader } from '@percent/admin-dashboard/common/components'
import { usePermissions, useQuery } from '@percent/admin-dashboard/common/hooks'
import {
  POSTHOG_FEATURE_FLAGS,
  usePostHogFeatureFlag
} from '@percent/admin-dashboard/common/hooks/usePostHogFeatureFlag'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { CardDashboard, Button, Text } from '@percent/lemonade'
import styles from './DonationMatchRequestDetailsHeader.module.scss'
import { DonationMatchRequestStatusBadge } from '@percent/admin-dashboard/app/donationMatchRequest/donationMatchRequestStatusBadge/DonationMatchRequestStatusBadge'

type DonationMatchRequestDetailsProps = {
  id: string
  partnerId: string
  createdAt: string
  status: DonationMatchRequestStatus
  showApproveModal: VoidFunction
  showRejectModal: VoidFunction
  showResetToPendingModal: VoidFunction
}

export function DonationMatchRequestDetailsHeader({
  id,
  partnerId,
  createdAt,
  status,
  showApproveModal,
  showRejectModal,
  showResetToPendingModal
}: DonationMatchRequestDetailsProps): JSX.Element {
  const { t } = useTranslation()
  const { adminService } = useServices()
  const { userHasPermissions } = usePermissions()
  const internalFgaDonationMatchRequestsFeatureFlag = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.INTERNAL_FGA_GIVING_DONATION_MATCH_REQUESTS
  )
  const [{ data, isLoading, errorMessage }] = useQuery(adminService.queries.getPartnerDetail, { id: partnerId })
  const canResetToPending =
    !internalFgaDonationMatchRequestsFeatureFlag || userHasPermissions('donation_match_requests:can_revert')

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <CardDashboard title={id}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <ul className={styles.headerList}>
          <li>
            <strong>{t('typography.partner')}</strong>&nbsp;
            <Text size="small">{data?.name}</Text>
          </li>
          <Divider orientation="vertical" flexItem />
          <li>
            <DonationMatchRequestStatusBadge status={status} />
          </li>
          <Divider orientation="vertical" flexItem />
          <li>
            <strong>{t('typography.submitted')}</strong>&nbsp;
            {dayJS(createdAt).format('D MMM YYYY, HH:mm')}
          </li>
        </ul>
        {(status === 'processing' || status === 'validating') && (
          <div
            style={{
              display: 'flex'
            }}
          >
            <div style={{ margin: 5 }}>
              <Button variant="reject" onClick={showRejectModal}>
                {t('button.reject')}
              </Button>
            </div>
            <div style={{ margin: 5 }}>
              <Button variant="approve" onClick={showApproveModal}>
                {t('button.approve')}
              </Button>
            </div>
          </div>
        )}
        {status === 'rejected' && canResetToPending && (
          <Button
            variant="secondary"
            onClick={showResetToPendingModal}
            data-testid="open-reset-match-request-to-pending-modal"
          >
            {t('button.donationMatchRequest.resetToPending')}
          </Button>
        )}
      </div>
    </CardDashboard>
  )
}
